import { z } from "zod";

import {
  MAX_MEDIA_BYTES,
  entityIdSchema,
  httpsUrlSchema,
  isoInstantSchema,
  mediaMimeTypeSchema,
  repoPathSchema,
  sha256Schema,
  type MediaMimeType,
} from "./primitives";

/**
 * Media metadata (SPEC §19).
 *
 * Bytes live in the content repository; only metadata lives in D1. Deleting D1
 * must therefore never lose an image — the index is rebuildable from Git
 * (SPEC §6.1).
 */

export const mediaSchema = z
  .object({
    id: entityIdSchema,
    siteId: entityIdSchema,

    /** Content hash, computed server-side. The dedupe key (SPEC §15). */
    sha256: sha256Schema,
    filename: z.string().min(1).max(255),
    mimeType: mediaMimeTypeSchema,
    size: z.number().int().positive().max(MAX_MEDIA_BYTES),

    /** Path inside the content repository. */
    githubPath: repoPathSchema,
    /**
     * Public CDN URL. Never `raw.githubusercontent.com` (SPEC §16).
     */
    publicUrl: httpsUrlSchema,

    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),

    createdAt: isoInstantSchema,
  })
  .strict()
  .refine((media) => !media.publicUrl.includes("raw.githubusercontent.com"), {
    message: "Media public URLs must not use raw.githubusercontent.com",
    path: ["publicUrl"],
  });

export type Media = z.infer<typeof mediaSchema>;

/**
 * What the editor receives back from an upload (AGENTS.md §14).
 *
 * The editor inserts `url` and nothing else; it has no idea the bytes are in
 * Git (AGENTS.md §13).
 */
export const mediaUploadResultSchema = z.object({
  id: entityIdSchema,
  url: httpsUrlSchema,
  path: repoPathSchema,
  filename: z.string(),
  mimeType: mediaMimeTypeSchema,
  size: z.number().int().positive(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  /** True when an identical file already existed and was reused (SPEC §69). */
  deduplicated: z.boolean(),
});

export type MediaUploadResult = z.infer<typeof mediaUploadResultSchema>;

/**
 * Build the deterministic storage path for a media asset (SPEC §17).
 *
 *   assets/posts/{entryId}/{hash}.{ext}
 *
 * Using the content hash as the filename removes collisions, cache-invalidation
 * pain and path-injection risk in one move — a user-supplied filename never
 * becomes part of the path.
 */
export function buildMediaPath(input: {
  entryId: string;
  sha256: string;
  mimeType: string;
  contentRoot?: string;
}): string {
  const extension = mimeTypeToExtension(input.mimeType);
  const prefix = input.contentRoot ? input.contentRoot.replace(/\/+$/, "") : "";

  return [prefix, "assets", "posts", input.entryId, `${input.sha256}.${extension}`]
    .filter((part) => part.length > 0)
    .join("/");
}

const EXTENSION_BY_MIME: Record<MediaMimeType, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "application/pdf": "pdf",
  "application/zip": "zip",
  "text/plain": "txt",
  "text/csv": "csv",
  "audio/mpeg": "mp3",
  "video/mp4": "mp4",
};

/** Map an allowed media MIME type to its canonical file extension. */
export function mimeTypeToExtension(mimeType: string): string {
  // The table is typed over the allow-list; the lookup takes `string` so
  // unsupported types surface here as a RangeError rather than at compile time
  // — callers handle user input that never saw this module's types.
  const extension = EXTENSION_BY_MIME[mimeType as MediaMimeType];
  if (!extension) {
    throw new RangeError(`Unsupported media MIME type: ${mimeType}`);
  }

  return extension;
}
